import React from 'react';
import ErrorBoundary from '../components/ErrorBoundary';

/**
 * Security Page
 * Single Responsibility: Display security information and practices
 */
const Security = () => {
  return (
    <ErrorBoundary section="Security">
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Security</h1>
            <p className="text-xl text-gray-600">
              How we protect your financial data
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Security Overview */}
          <div className="bg-white rounded-lg shadow p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Security</h2>
            <p className="text-gray-700">
              At eloNest, we take the security of your financial data seriously. This page describes the
              measures in place today.
            </p>
          </div>

          {/* Security Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure Connections</h3>
              <p className="text-gray-600">
                Connections to eloNest use HTTPS.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure Authentication</h3>
              <p className="text-gray-600">
                Passwords are hashed with bcrypt. You can also turn on optional two-factor authentication.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Controls</h3>
              <p className="text-gray-600">
                Accounts have roles that limit which pages a member can use.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Card Payments</h3>
              <p className="text-gray-600">
                Card details are entered on Stripe's hosted pages. eloNest never receives your full card number.
              </p>
            </div>
          </div>

          {/* User Responsibilities */}
          <div className="bg-white rounded-lg shadow p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Role in Security</h2>
            <p className="text-gray-700 mb-4">
              Security is a shared responsibility. Here's how you can help keep your account secure:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Use a strong, unique password for your account</li>
              <li>Enable two-factor authentication if available</li>
              <li>Never share your login credentials with anyone</li>
              <li>Log out when using shared or public computers</li>
              <li>Keep your devices and browsers updated</li>
              <li>Be cautious of phishing emails and suspicious links</li>
              <li>Review your account activity regularly</li>
            </ul>
          </div>

          {/* Reporting Security Issues */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3">Report a Security Issue</h2>
            <p className="text-gray-700 mb-4">
              If you discover a security vulnerability or have concerns about our security practices, 
              please report it to us immediately:
            </p>
            <a href="mailto:security@elonest.com" className="text-blue-600 hover:text-blue-700 font-medium">
              security@elonest.com
            </a>
            <p className="text-sm text-gray-600 mt-2">
              We take all security reports seriously and will investigate promptly.
            </p>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Security;

